import React, { useState, useEffect } from 'react';
import { Button, Drawer, Dropdown, Menu, MenuProps } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import './Header.scss';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logoIcon from 'assets/images/logo.png';
import profileIcon from 'assets/images/profile.png';
import languageIcon from 'assets/images/language.png';
import MobileMenu from '../mobileMenu/MobileMenu';
import { createMainMenuItems } from 'routes/menu';
import qrCodeIcon from 'assets/images/qr_code_small.png';
import { derivePathKeys } from 'utils/commonUtils';
import {
  buildBenefitsPath,
  buildProfilePath,
  buildLandingPath,
  buildLoginPath,
} from 'utils/pathBuilder';
import downArrowIcon from 'assets/images/down_arrow.png';
import { useSession } from 'context/SessionContext';
import chevronImg from 'assets/images/chevron.png';
import { MemberProfile } from 'types/dto/profile';
import { useMemberQRCode } from 'hooks/useMemberQRCode';
import MemberQRCode from './MemberQRCode';

interface HeaderProps {
  profile: MemberProfile | null;
}

const Logo = ({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) => (
  <img
    src={logoIcon}
    alt="logo"
    className={`logo ${disabled ? 'logo-disabled' : ''}`}
    onClick={disabled ? undefined : onClick}
  />
);

const Header = ({ profile }: HeaderProps) => {
  const { t, i18n } = useTranslation('layout');
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [qrCodeDrawerVisible, setQRCodeDrawerVisible] = useState(false);
  const { session, clearSession } = useSession();
  const DISABLED_PATHS = ['email', 'password', 'registration', 'signin'];

  const isLogoDisabled = DISABLED_PATHS.some(path => location.pathname.includes(path));

  const customerName = `${profile?.loyalty_account?.first_name || ''} ${profile?.loyalty_account?.last_name || ''}`.trim();

  // One fetch per session: Header lives in the persistent Layout route element, and both QR renders
  // (account menu + mobile drawer) share this single payload at different display sizes.
  const memberQRCode = useMemberQRCode(profile?.loyalty_account?.unique_id);

  const isActivatedUser = !isLogoDisabled && !!session?._tk && session?._at === 'password';

  const accountDropdownItems: MenuProps['items'] = [
    {
      key: 'qrcode',
      label: (
        <div style={{
          padding: '8px',
          textAlign: 'center'
        }}>
          <div className='account-menu-qrcode'>
            <MemberQRCode {...memberQRCode} size={80} />
            {i18n.language === 'zh'
              ? profile?.loyalty_account?.first_name + ' ' + profile?.loyalty_account?.last_name
              : profile?.loyalty_account?.first_name + ' ' + profile?.loyalty_account?.last_name}
          </div>
        </div>
      ),
      disabled: true,
    },
    {
      key: 'account',
      label: <div className={'account-menu-item'}>{t('menu.profile')}</div>,
    },
    {
      key: 'membership',
      label: (
        <div className={'account-menu-item'}>{t('menu.membershipScheme')}</div>
      ),
    },
    { type: 'divider' },
    {
      key: 'logout',
      label: <div className={'account-menu-item'}>{t('menu.signOut')}</div>,
    },
  ];

  // Language switch menu items
  const languageDropdownItems: MenuProps['items'] = [
    {
      key: 'en',
      label: <div className={'account-menu-item'}>{t('language.english')}</div>,
    },
    {
      key: 'zh',
      label: <div className={'account-menu-item'}>{t('language.chinese')}</div>,
    },
  ];

  const menuItems = createMainMenuItems(t);

  const handleLanguageChange: MenuProps['onClick'] = (e) => {
    const newLang = e.key;
    const targetPath = location.pathname.replace(`/${i18n.language}`, `/${newLang}`);

    React.startTransition(() => {
      navigate(targetPath, {
        replace: true,
        state: location.state
      });
    });
  };

  const handleAccountClick: MenuProps['onClick'] = async (e) => {
    // console.log('Account clicked', e);
    if (e.key === 'account') {
      navigate(buildProfilePath());
    } else if (e.key === 'membership') {
      navigate(buildBenefitsPath());
    } else if (e.key === 'logout') {
      clearSession();
      navigate(buildLoginPath(), { replace: true });
    }
  };

  const onClick: MenuProps['onClick'] = (e) => {
    // Filter out 'rc-menu-more' from keyPath to handle collapsed menu items
    const filteredKeyPath = e.keyPath.filter((key) => key !== 'rc-menu-more');
    const cleanPath = filteredKeyPath.reverse().join('/');

    const lang = i18n.language || 'en';
    const fullPath = `/${lang}/${cleanPath}`;

    navigate(fullPath);
  };

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  useEffect(() => {
    const { parent, child } = derivePathKeys(
      location.pathname,
      String(process.env.PUBLIC_URL)
    );
    setSelectedKeys(child ? [child] : parent ? [parent] : []);
  }, [location.pathname]);

  const handleQRCodeOpen = () => {
    setQRCodeDrawerVisible(true);
  };

  const handleMobileMenuOpen = () => {
    setMobileMenuVisible(true);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuVisible(false);
  };

  const handleQRCodeDrawerClose = () => {
    setQRCodeDrawerVisible(false);
  };

  const handleLogoClick = () => {
    navigate(buildLandingPath());
  };

  const renderAccountDropdown = () => (
    <Dropdown
      menu={{
        items: accountDropdownItems,
        onClick: handleAccountClick,
      }}
      trigger={['click']}
    >
      <Button
        type="link"
        icon={
          <img
            src={profileIcon}
            alt="Profile"
            className="profile-icon"
          />
        }
      >
        <div className="profile-button-container">
          <span className="dropdown-button-title">
            {t('header.welcome', { name: customerName })}
          </span>
          <img
            src={downArrowIcon}
            alt="Down Arrow"
            className="down-arrow-icon"
          />
        </div>
      </Button>
    </Dropdown>
  );

  const renderLanguageDropdown = () => (
    <Dropdown
      menu={{
        items: languageDropdownItems,
        onClick: handleLanguageChange,
      }}
      trigger={['click']}
    >
      <Button
        type="link"
        className="language-button"
        icon={
          <img src={languageIcon} alt="Language" className="language-icon" />
        }
      >
        <span className="dropdown-button-title">
          {i18n.language === 'zh'
            ? t('language.chinese')
            : t('language.english')}
        </span>
      </Button>
    </Dropdown>
  );

  return (
    <div className="nav-header-container" data-testid="nav-header-container">
      {/* Mobile Menu Button - only visible on small screens */}
      <Button
        type="link"
        icon={<MenuOutlined />}
        onClick={handleMobileMenuOpen}
        className="mobile-menu-button"
      />

      <Logo onClick={handleLogoClick} disabled={isLogoDisabled} />

      {
        isActivatedUser ? (
          ///* QR Code Button - only visible on small screens */}
          <Button
            type="link"
            icon={<img src={qrCodeIcon} className="qr-code-icon" />}
            onClick={handleQRCodeOpen}
            className="mobile-menu-button"
          />)
          : <div className="mobile-menu-button-placeholder"></div>
      }


      {isActivatedUser ? (
        <>
          <Menu
            mode="horizontal"
            onClick={onClick}
            className="header-menu"
            // defaultOpenKeys={subPathArray}
            selectedKeys={selectedKeys}
            onSelect={(info) => {
              setSelectedKeys(info.selectedKeys);
            }}
            openKeys={openKeys}
            onOpenChange={(keys) => {
              setOpenKeys(keys);
            }}
            items={menuItems}
          // inlineCollapsed={!isOpen}
          />

          <div className="right-section">
            {renderAccountDropdown()}
            {renderLanguageDropdown()}
          </div>
        </>
      ) : (
        <div className="unauthenticated-section">
          {renderLanguageDropdown()}
        </div>
      )}

      {/* Mobile Menu Drawer */}
      <MobileMenu visible={mobileMenuVisible} onClose={handleMobileMenuClose} />

      <Drawer
        title={null}
        placement="right"
        onClose={handleQRCodeDrawerClose}
        open={qrCodeDrawerVisible}
        width={'100%'}
        className="mobile-menu-drawer"
        closable={false}
        styles={{ body: { padding: 0 } }}
      >
        <div className="mobile-qrcode-container">
          <div className="mobile-qrcode-header">
            <Button
              type="text"
              icon={<img src={chevronImg} className="chevron-icon"></img>}
              onClick={handleQRCodeDrawerClose}
              className="close-button"
            />
          </div>
          <div className='mobile-qrcode-content'>
            <div className='mobile-qrcode'>
              <MemberQRCode {...memberQRCode} size={170} />
              <div>{t('header.scanQRCode')}</div>
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default Header;